// components/AttendeesSummary.js
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { theme } from '../theme';
import { statusColors } from '../utils/statusColors';
import { useAttendeeStateTranslations, getStatePriority } from '../constants/attendeeStates';
import Typography from '../components/Typography';

const AttendeesSummary = ({ capacity, attendees }) => {
  const { t } = useTranslation('manageEventAttendees');
  const { getStateLabel } = useAttendeeStateTranslations();

  // Calculamos los asistentes por estado
  const attendeesByStatus = attendees.reduce((acc, attendee) => {
    const status = attendee.status;
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Total de asistentes confirmados (attending o confirmation_pending)
  const confirmedAttendees = attendees.filter(
    (attendee) => attendee.status === 'attending' || attendee.status === 'confirmation_pending' || attendee.status === 'cancellation_pending'
  ).length;

  // Total de asistentes (para mantener compatibilidad con el resto del código)
  const totalAttendees = confirmedAttendees;

  // Capacidad efectiva para evitar división por cero
  const effectiveCapacity = capacity || totalAttendees || 1;

  // Calculamos el porcentaje para cada estado
  const statusPercentages = Object.keys(attendeesByStatus).reduce((acc, status) => {
    acc[status] = (attendeesByStatus[status] / effectiveCapacity) * 100;
    return acc;
  }, {});



  return (
    <SummaryWrapper>
      <TitleWrapper>
        <Typography $variant="title-3-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold" as='h2'>{t('attendeesSummary.title')}</Typography>
      </TitleWrapper>

      <AttendanceMetrics>
        <AttendanceData>
          <AttendanceDataBlock>
            <Typography $variant='title-3-semibold' as='p'>
              {confirmedAttendees}
            </Typography>
            <Typography $variant='body-1-semibold' color={theme.colors.defaultStrong}>
              {t('attendeesSummary.attendees')}
            </Typography>
          </AttendanceDataBlock>
          <AttendanceDataBlock>
            <Typography $variant='body-1-semibold'>
              {t('attendeesSummary.max')}
            </Typography>
            <Typography $variant='title-3-semibold' as='p' color={theme.colors.defaultStrong}>
              {/* {capacity} */}
              {capacity}
            </Typography>
          </AttendanceDataBlock>
        </AttendanceData>

        <AttendanceChart>
          <BarChartContainer>
            <BarChart>
              {Object.keys(statusPercentages)
                .sort((a, b) => getStatePriority(b) - getStatePriority(a))
                .map((status) => (
                  <BarSegment
                    key={status}
                    style={{
                      width: `${statusPercentages[status]}%`,
                      backgroundColor: statusColors[status],
                    }}
                    title={`${attendeesByStatus[status]} ${status}`}
                  />
                ))}
            </BarChart>
            <Typography $variant='body-3-medium' as='p' color={theme.colors.defaultStrong}>
              {Math.round((totalAttendees / effectiveCapacity) * 100)}% {t('attendeesSummary.occupied')}
            </Typography>
          </BarChartContainer>
          <AttendanceStatusList>
            {Object.keys(attendeesByStatus)
              .sort((a, b) => getStatePriority(b) - getStatePriority(a))
              .map((status) => (
                <StatusItem key={status}>
                  <StatusIndicator style={{ backgroundColor: statusColors[status] }} />
                  <Typography $variant='body-2-medium' color={statusColors[status]}>
                    {attendeesByStatus[status]} {getStateLabel(status)}
                  </Typography>
                </StatusItem>
              ))}
          </AttendanceStatusList>
        </AttendanceChart>
      </AttendanceMetrics>
    </SummaryWrapper>
  );
};



AttendeesSummary.propTypes = {
  attendees: PropTypes.array.isRequired,
  capacity: PropTypes.number.isRequired,
};

export default AttendeesSummary;


// Estilizado
const SummaryWrapper = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: flex-start;
  padding-bottom: ${({ theme }) => theme.sizing.xl};
  padding-top: ${({ theme }) => theme.sizing.md};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-bottom: ${({ theme }) => theme.sizing.md};
    padding-top: 0;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 40px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 32px;
  }
`;

const AttendanceMetrics = styled.div`
align-items: center;
display:flex;
flex-direction: column;
gap: ${({ theme }) => theme.sizing.xs};
justify-content:space-between;
width:100%;
`;

const AttendanceData = styled.div`
align-items:center;
display:flex;
flex-direction:row;
gap:${({ theme }) => theme.sizing.md};
justify-content:space-between;
width: 100%;
`;

const AttendanceDataBlock = styled.div`
align-items: center;
display: flex;
flex-direction: row;
gap: ${({ theme }) => theme.sizing.xs};
`;

const AttendanceChart = styled.div`
width: 100%;
`;

const BarChartContainer = styled.div`
display: flex;
flex-direction: column;
gap: ${({ theme }) => theme.sizing.xs};
`;

const BarChart = styled.div`
background-color: ${({ theme }) => theme.fill.defaultWeak};
border-radius: ${({ theme }) => theme.sizing.xs};
display: flex;
height: ${({ theme }) => theme.sizing.xs};
overflow: hidden;
width: 100%;
`;


const BarSegment = styled.div`
border-right: 1px solid ${({ theme }) => theme.border.defaultWeak};
height: 100%;

  &:last-child {
  border-right: none;
}
`;

const AttendanceStatusList = styled.div`
display: flex;
flex-direction: row;
gap: ${({ theme }) => theme.sizing.sm};
justify-content: flex-start;
margin-top: ${({ theme }) => theme.sizing.sm};
width: 100%;
flex-wrap: wrap;
`;

const StatusItem = styled.div`
align-items: center;
display: flex;
gap: ${({ theme }) => theme.sizing.xs};
`;

const StatusIndicator = styled.div`
border-radius: 8px;
height: ${({ theme }) => theme.sizing.xs};
width: ${({ theme }) => theme.sizing.xs};
`;